import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, Users, Clock, Award, Zap, HeartHandshake } from 'lucide-react';

const WhyUs = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      badge: '¿Por qué Vegsoft Solutions?',
      title: 'Creamos Productos Digitales que',
      titleHighlight: 'Generan Resultados Reales',
      subtitle: 'Más de 14 años diseñando tecnología y automatizaciones para empresas. No somos solo desarrolladores: somos tu socio estratégico de producto e innovación.',
      stats: [
        { number: '50+', label: 'Productos & Soluciones' },
        { number: '30+', label: 'Empresas Escalando' },
        { number: '14+', label: 'Años de Experiencia' },
        { number: '98%', label: 'Tasa de Retención' }
      ],
      features: [
        {
          icon: Clock,
          title: 'Time-to-Market Acelerado',
          description: 'Prototipos interactivos en días, no meses. Validamos tu producto rápidamente para llegar al mercado antes que la competencia.'
        },
        {
          icon: Zap,
          title: 'Automatización & Eficiencia con IA',
          description: 'Eliminamos cuellos de botella y tareas manuales repetitivas integrando agentes autónomos y flujos inteligentes 24/7.'
        },
        {
          icon: CheckCircle,
          title: 'Prototipo Inicial Sin Costo',
          description: 'Diseñamos un prototipo funcional para que evalúes la experiencia y alcance técnico antes de comprometer cualquier inversión.'
        },
        {
          icon: Users,
          title: 'Equipo de Producto Dedicado',
          description: 'Product Managers, Diseñadores UX/UI, Ingenieros de IA y Desarrolladores Senior enfocados en métricas de negocio.'
        },
        {
          icon: Award,
          title: 'Calidad & Seguridad Garantizada',
          description: 'Testing automatizado, CI/CD, cifrado de datos y código modular de fácil escalabilidad.'
        },
        {
          icon: HeartHandshake,
          title: 'Partnership & Evolución Continua',
          description: 'Acompañamiento post-lanzamiento, monitoreo proactivo y optimización continua de tus canales digitales.'
        }
      ]
    },
    en: {
      badge: 'Why Vegsoft Solutions?',
      title: 'We Build Digital Products that',
      titleHighlight: 'Drive Real Revenue',
      subtitle: 'Over 14 years designing technology and automation for companies. We are not just coders: we are your strategic product and innovation partner.',
      stats: [
        { number: '50+', label: 'Products & Solutions' },
        { number: '30+', label: 'Companies Scaling' },
        { number: '14+', label: 'Years of Experience' },
        { number: '98%', label: 'Retention Rate' }
      ],
      features: [
        {
          icon: Clock,
          title: 'Accelerated Time-to-Market',
          description: 'Interactive prototypes in days, not months. We validate your product fast so you can launch ahead of competition.'
        },
        {
          icon: Zap,
          title: 'Automation & AI Efficiency',
          description: 'We eliminate manual bottlenecks and repetitive workflows by deploying 24/7 AI agents and smart integrations.'
        },
        {
          icon: CheckCircle,
          title: 'No-Cost Initial Prototype',
          description: 'We design and structure an interactive prototype so you can evaluate the experience before committing investment.'
        },
        {
          icon: Users,
          title: 'Dedicated Product Team',
          description: 'Product Managers, UX/UI Designers, AI Engineers, and Senior Developers aligned with your business KPIs.'
        },
        {
          icon: Award,
          title: 'Guaranteed Quality & Security',
          description: 'Automated testing, continuous deployment, data encryption, and clean modular code built to scale.'
        },
        {
          icon: HeartHandshake,
          title: 'Partnership & Continuous Evolution',
          description: 'Post-launch support, proactive monitoring, and constant feature optimization for your digital channels.'
        }
      ]
    }
  };

  const c = content[language];

  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-20 lg:py-28 overflow-hidden bg-white border-t border-slate-100"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="stripe-badge mb-4 inline-flex">
            <span>🛡️</span>
            <span>{c.badge}</span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight text-[#0F172A]">
            {c.title}{' '}
            <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">
              {c.titleHighlight}
            </span>
          </h2>
          <p className="text-base lg:text-lg text-slate-600 leading-relaxed font-normal">
            {c.subtitle}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 mb-14">
          {c.stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 lg:p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-all shadow-sm"
            >
              <div className="font-heading font-extrabold text-4xl lg:text-5xl text-[#0F172A] tracking-tight mb-2">
                {stat.number}
              </div>
              <div className="text-xs sm:text-sm text-slate-500 font-semibold">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {c.features.map((feature, index) => {
            const Icon = feature.icon;
            const colors = ['#4F46E5', '#10B981', '#06B6D4', '#7C3AED', '#F59E0B', '#3B82F6'];
            const bgs = ['#EEF2FF', '#ECFDF5', '#ECFEFF', '#F5F3FF', '#FEF3C7', '#EFF6FF'];
            const accent = colors[index % colors.length];
            const accentBg = bgs[index % bgs.length];

            return (
              <div
                key={index}
                data-testid={`why-us-feature-${index}`}
                className="stripe-card p-7 flex flex-col justify-between group"
              >
                <div>
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 shadow-sm"
                    style={{ background: accentBg }}
                  >
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-2 group-hover:text-[#4F46E5] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed font-normal">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
